/// <amd-module name="Repos/Views/FilesHubActions/Components/CommitChangesPanel" />
import * as React from "react";
import { GitPush, GitVersionDescriptor, GitVersionType } from "Repos/Common/Contracts/Git";
import { getInvalidFileNameMessage } from "Repos/Common/Util/File";
import { getInvalidBranchNameMessage } from "Repos/Common/Util/Ref";
import { IGitItem } from "Repos/Files/Contracts";
import * as Resources from "Repos/Views/FilesHubActions/Resources";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IRegionComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { FileInput, FileInputResult, FileInputUpdateEventData } from "VSSUI/FileInput";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { ITextField, TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";

export interface ICommitChangesDetails {
    branch: string;
    comment: string;
    createPullRequest: boolean;
    files?: FileInputResult[];
    renameTo?: string;
}

export interface ICommitChangesContext {
    addPushCompleteListener: (listener: PushCompleteListener) => void;
}

export declare type PushCompleteListener = (push: GitPush, newBranchName?: string) => void | Promise<void>;

export interface ICommitChangesPanelProps {
    version: GitVersionDescriptor;
    comment: string;
    commitChangesContext: ICommitChangesContext;
    errorMessage: IReadonlyObservableValue<string>;
    initialFiles?: FileList;
    renameFrom?: string;
    showOverlay: IReadonlyObservableValue<boolean>;
    uploadExistingItems?: IReadonlyObservableArray<IGitItem>;
    uploadTo?: string;
    onDismiss: (details?: ICommitChangesDetails) => void;
}

// Set max file size to edit at 20 MB. The maximum size of the payload on the server is 25 MB. Use something
// less to account for JSON payload overhead + allowing for some unicode characters (transferred as multiple bytes).
// This is not exact. We may be enforcing a slightly lower limit here than what would normally be allowed.
// We could also not detect this state if the file contained a whole bunch of unicode text. That's okay, in that
// case the server will reject the request. This just helps us have a better experience where we fail earlier and
// give a better error message.
const maxFileContentSize = 20 * 1024 * 1024;

export class CommitChangesPanel extends VssComponent<ICommitChangesPanelProps> {
    private renameAs: ObservableValue<string> | undefined;
    private comment: ObservableValue<string>;
    private branchName: ObservableValue<string>;
    private fieldsValid: ObservableValue<boolean>;
    private createPullRequest = new ObservableValue(false);
    private showCreatePullRequest = new ObservableValue(false);

    private renameAsError = new ObservableValue(false);
    private renameAsErrorMessage = new ObservableValue("");

    private commentError = new ObservableValue(false);
    private commentErrorMessage = new ObservableValue("");

    private branchError = new ObservableValue(false);
    private branchErrorMessage = new ObservableValue("");

    private userChangedComment = false;
    private userChangedName = false;

    private uploadData: FileInputUpdateEventData;
    private updateExistingFiles: { [name: string]: IGitItem } = {};

    constructor(props: ICommitChangesPanelProps) {
        super(props);

        if (props.renameFrom) {
            this.renameAs = new ObservableValue(props.renameFrom);
            this.renameAsError.value = true;
            this.renameAsErrorMessage.value = Resources.RenameAsDifferentName;
        }

        this.comment = new ObservableValue(props.comment);
        this.branchName = new ObservableValue(props.version.versionType === GitVersionType.Branch ? props.version.version : "");

        this.branchErrorMessage.value = getInvalidBranchNameMessage(this.branchName.value) || "";
        this.branchError.value = !!this.branchErrorMessage.value;

        this.fieldsValid = new ObservableValue(!props.renameFrom && !props.uploadTo && !!this.branchName.value);

        if (this.props.uploadExistingItems) {
            this.updateExistingItemsHash();
            this.observe([
                {
                    propertyName: "uploadExistingItems",
                    observer: () => {
                        this.updateExistingItemsHash();
                        this.updateFileInputComment();
                        this.validate();
                    }
                }
            ]);
        }
    }

    public render() {
        return (
            <Observer valid={this.fieldsValid} showOverlay={this.props.showOverlay}>
                {(props: { valid: boolean; showOverlay: boolean }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = [
                        {
                            onClick: () => {
                                onDismiss();
                            },
                            text: Resources.Cancel
                        },
                        {
                            onClick: this.submit,
                            disabled: !props.valid,
                            primary: true,
                            text: Resources.Commit
                        }
                    ];
                    return (
                        <Panel
                            defaultActiveElement={this.props.renameFrom ? ".repos-commit-panel-rename-input" : ".repos-commit-panel-comment-input"}
                            lightDismiss={false}
                            onDismiss={() => {
                                onDismiss();
                            }}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: Resources.Commit }}
                            footerButtonProps={buttons}
                        >
                            <div className="rhythm-vertical-16 flex-grow">
                                <Observer errorMessage={this.props.errorMessage}>
                                    {(props: { errorMessage: string }) => {
                                        return (
                                            props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                        );
                                    }}
                                </Observer>
                                {this.renameAs && (
                                    <FormItem
                                        label={Resources.RenameAs}
                                        error={this.userChangedName && this.renameAsError}
                                        message={this.userChangedName && this.renameAsErrorMessage}
                                    >
                                        <TextField
                                            ref={this.resolveRenameRef}
                                            onChange={this.onRenameAsChanged}
                                            onKeyDown={this.onRenameAsKeyDown}
                                            value={this.renameAs}
                                            inputClassName="repos-commit-panel-rename-input"
                                        />
                                    </FormItem>
                                )}
                                {this.props.uploadTo && (
                                    <FileInput
                                        fileNamesCaseSensitive={true}
                                        initialFiles={this.props.initialFiles}
                                        limitMessageFormatter={message => message + Resources.FileSizeLimitWorkaround}
                                        maximumTotalFileSize={maxFileContentSize}
                                        updateHandler={this.onFileInputUpdated}
                                    />
                                )}
                                <FormItem label={Resources.Comment} error={this.commentError} message={this.commentErrorMessage}>
                                    <TextField
                                        onChange={this.onCommentChanged}
                                        multiline={true}
                                        value={this.comment}
                                        inputClassName="repos-commit-panel-comment-input"
                                        rows={5}
                                    />
                                </FormItem>
                                <FormItem label={Resources.BranchName} error={this.branchError} message={this.branchErrorMessage}>
                                    <TextField onChange={this.onBranchNameChanged} value={this.branchName} />
                                </FormItem>
                                <WrappedComponent<IRegionComponentProperties>
                                    dependencies={["ms.vss-code-web.commit-changes-panel-extensions"]}
                                    wrappedType="ms.vss-web.region"
                                    wrappedProps={{
                                        componentRegion: "commitChangesPanelExtensions",
                                        childComponentProperties: { commitChangesContext: this.props.commitChangesContext }
                                    }}
                                />
                                <Observer showCreatePullRequest={this.showCreatePullRequest}>
                                    {(props: { showCreatePullRequest: boolean }) => {
                                        return (
                                            props.showCreatePullRequest && (
                                                <Checkbox
                                                    label={Resources.CreateAPullRequest}
                                                    checked={this.createPullRequest}
                                                    onChange={(ev, value) => {
                                                        this.createPullRequest.value = value;
                                                    }}
                                                />
                                            )
                                        );
                                    }}
                                </Observer>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private submit = (): void => {
        const details = {
            branch: this.branchName.value,
            comment: this.comment.value,
            createPullRequest: this.showCreatePullRequest.value && this.createPullRequest.value,
            files: this.uploadData ? this.uploadData.files.map(f => f.result) : undefined,
            renameTo: this.renameAs ? this.renameAs.value : undefined
        };

        this.props.onDismiss(details);
    };

    private resolveRenameRef = (renameTextField: ITextField | null) => {
        if (renameTextField) {
            renameTextField.focus();

            const lastDot = this.props.renameFrom!.lastIndexOf(".");
            if (lastDot > 0) {
                renameTextField.setSelectionRange(0, lastDot);
            } else {
                renameTextField.select();
            }
        }
    };

    private onRenameAsChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, renameAs: string): void => {
        this.renameAs!.value = renameAs;
        this.userChangedName = true;

        if (!this.userChangedComment) {
            this.comment.value = format(Resources.RenameCommentFormat, this.props.renameFrom, renameAs);
        }

        let valid = true;
        if (!renameAs) {
            this.renameAsErrorMessage.value = Resources.RenameAsCannotBeEmpty;
            valid = false;
        } else {
            const invalidFileNameMessage = getInvalidFileNameMessage(renameAs);
            if (invalidFileNameMessage) {
                this.renameAsErrorMessage.value = invalidFileNameMessage;
                valid = false;
            } else if (renameAs === this.props.renameFrom) {
                this.renameAsErrorMessage.value = Resources.RenameAsDifferentName;
                valid = false;
            } else {
                this.renameAsErrorMessage.value = "";
            }
        }

        this.renameAsError.value = !valid;
        this.validate();
    };

    private onRenameAsKeyDown = (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (ev.which === KeyCode.enter && this.fieldsValid.value) {
            this.submit();
        }
    };

    private onCommentChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, comment: string): void => {
        this.comment.value = comment;
        this.userChangedComment = true;

        let valid = true;
        if (!comment) {
            this.commentErrorMessage.value = Resources.CommentCannotBeEmpty;
            valid = false;
        } else {
            this.commentErrorMessage.value = "";
        }

        this.commentError.value = !valid;

        this.validate();
    };

    private onBranchNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, branchName: string): void => {
        this.branchName.value = branchName;
        this.showCreatePullRequest.value = this.props.version.versionType !== GitVersionType.Branch || branchName !== this.props.version.version;

        this.branchErrorMessage.value = getInvalidBranchNameMessage(branchName) || "";
        this.branchError.value = !!this.branchErrorMessage.value;

        this.validate();
    };

    private onFileInputUpdated = (updateEvent: FileInputUpdateEventData): void => {
        this.uploadData = updateEvent;
        this.updateFileInputComment();
        this.validate();
    };

    private updateExistingItemsHash(): void {
        this.updateExistingFiles = {};
        for (const item of this.props.uploadExistingItems!.value) {
            if (!item.isFolder) {
                this.updateExistingFiles[item.name.toLocaleLowerCase()] = item;
            }
        }
    }

    private updateFileInputComment(): void {
        const uploadData = this.uploadData;
        if (uploadData && !this.userChangedComment) {
            if (uploadData.files.length === 0) {
                this.comment.value = "";
            } else {
                const hasUpdates = this.uploadData.files.some(f => this.updateExistingFiles[f.result.name.toLocaleLowerCase()]);
                if (uploadData.files.length === 1) {
                    if (hasUpdates) {
                        this.comment.value = format(Resources.UpdatedFileCommentFormat, uploadData.files[0].result.name);
                    } else {
                        this.comment.value = format(Resources.AddedFileToCommentFormat, uploadData.files[0].result.name, this.props.uploadTo);
                    }
                } else {
                    this.comment.value = format(
                        hasUpdates ? Resources.UpdatedFilesCommentFormat : Resources.AddedFilesCommentFormat,
                        uploadData.files.length,
                        this.props.uploadTo
                    );
                }
            }
        }
    }

    private validate(): void {
        let valid = !this.renameAsError.value && !this.commentError.value && !this.branchError.value && !!this.comment.value;
        if (valid && this.props.uploadTo) {
            valid =
                this.uploadData && !this.uploadData.loading && this.uploadData.files.length > 0 && !this.uploadData.files.some(f => f.result.isError);
        }

        if (valid !== this.fieldsValid.value) {
            this.fieldsValid.value = valid;
        }
    }
}
